import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout';
import { getProposalProcessingStatus, getLeadProposals, getWorkerOutputs, forceGenerateProposal } from '../../api/proposals';
import { ProposalGenerationProgress } from '../../components/proposal';

const PROCESSING_STATUSES = ['queued', 'workers_running', 'rating_proposal', 'generating_proposal'];

const ProposalGeneratingPage = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('queued');
  const [workersCompleted, setWorkersCompleted] = useState(0);
  const [workerOutputs, setWorkerOutputs] = useState([]);
  const [proposalId, setProposalId] = useState(null);
  const [rating, setRating] = useState(null);
  const [ratingReasons, setRatingReasons] = useState(null);
  const [showLowRatingModal, setShowLowRatingModal] = useState(false);
  const [forceGenerating, setForceGenerating] = useState(false);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    let interval;

    const checkStatus = async () => {
      try {
        const proposalsRes = await getLeadProposals(leadId);
        const proposals = proposalsRes.data?.proposals || [];
        if (proposals.length === 0) return;

        const processing = proposals.find((p) =>
          PROCESSING_STATUSES.includes(p.generation_status)
        );
        // Also match rating_below_threshold so we can show the modal
        const belowThreshold = proposals.find((p) => p.generation_status === 'rating_below_threshold');
        const target = processing || belowThreshold || proposals[0];

        if (!proposalId && target.id) setProposalId(target.id);

        if (!processing && !belowThreshold && target.generation_status === 'completed') {
          if (proposalId && proposalId === target.id) {
            navigate(`/leads/${leadId}/proposals/${target.id}`);
          }
          return;
        }

        const res = await getProposalProcessingStatus(leadId, target.id);
        setStatus(res.data.generation_status || 'queued');
        setWorkersCompleted(res.data.workers_completed || 0);

        // Capture rating data from polling response
        if (res.data.rating != null) {
          setRating(res.data.rating);
          setRatingReasons(res.data.rating_reasons || null);
        }

        // Fetch worker outputs for live progress
        try {
          const workersRes = await getWorkerOutputs(leadId, target.id);
          setWorkerOutputs(workersRes.data?.worker_outputs || []);
        } catch { /* optional */ }

        // Handle low rating — show confirmation modal
        if (res.data.generation_status === 'rating_below_threshold') {
          setShowLowRatingModal(true);
          // Stop polling
          if (interval) clearInterval(interval);
          return;
        }

        if (!res.data.is_processing && res.data.generation_status === 'completed') {
          navigate(`/leads/${leadId}/proposals/${target.id}`);
        }
      } catch { /* ignore */ }
    };

    checkStatus();
    interval = setInterval(checkStatus, 3000);
    return () => clearInterval(interval);
  }, [leadId, navigate, proposalId]);

  const handleRetry = () => {
    navigate(`/leads/${leadId}`);
  };

  const handleImproveInput = () => {
    navigate(`/leads/${leadId}/questionnaire`);
  };

  const handleForceGenerate = async () => {
    if (!proposalId) return;
    setForceGenerating(true);
    try {
      await forceGenerateProposal(leadId, proposalId);
      setShowLowRatingModal(false);
      setStatus('generating_proposal');
      // Restart polling by updating proposalId to re-trigger useEffect
    } catch {
      // If force-generate fails, stay on modal
    } finally {
      setForceGenerating(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto py-12 px-4">
        <ProposalGenerationProgress
          status={status}
          workersCompleted={workersCompleted}
          workerOutputs={workerOutputs}
          startTime={startTimeRef.current}
          onRetry={handleRetry}
          rating={rating}
          ratingReasons={ratingReasons}
        />
      </div>

      {/* Low Rating Confirmation Modal */}
      {showLowRatingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="px-6 py-5 bg-amber-50 border-b border-amber-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <span className="text-xl font-bold text-amber-700">{rating || '?'}</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-amber-900">AI Expert Analysis: {rating}/10</h3>
                  <p className="text-sm text-amber-700">Expert analysis rated this deal below the quality threshold</p>
                </div>
              </div>
            </div>

            <div className="px-6 py-5 max-h-80 overflow-y-auto">
              {ratingReasons && ratingReasons.length > 0 ? (
                <div className="space-y-2">
                  {ratingReasons.map((reason, idx) => (
                    <div
                      key={idx}
                      className={`rounded-lg border px-3 py-2.5 flex items-start gap-2.5 ${
                        reason.impact === 'negative'
                          ? 'bg-red-50 border-red-200'
                          : reason.impact === 'positive'
                          ? 'bg-green-50 border-green-200'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      {reason.impact === 'negative' ? (
                        <svg className="w-4 h-4 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      ) : reason.impact === 'positive' ? (
                        <svg className="w-4 h-4 text-green-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                      <div>
                        <span className={`text-[10px] font-semibold uppercase tracking-wide ${
                          reason.impact === 'negative' ? 'text-red-600' :
                          reason.impact === 'positive' ? 'text-green-600' : 'text-gray-500'
                        }`}>
                          {(reason.category || '').replace(/_/g, ' ')}
                        </span>
                        <p className="text-xs text-gray-700 leading-relaxed">{reason.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No detailed reasons available.</p>
              )}
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                onClick={handleImproveInput}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Improve Input
              </button>
              <button
                onClick={handleForceGenerate}
                disabled={forceGenerating}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {forceGenerating ? (
                  <span className="inline-flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    Generating...
                  </span>
                ) : 'Force Generate Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ProposalGeneratingPage;
